import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "./AuthContext";
import api from "../lib/api";

const CartContext = createContext(null);

const load = (key) => {
  try { return JSON.parse(localStorage.getItem(key)) || []; } catch { return []; }
};

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState(() => load("hc_cart"));
  const [wishlist, setWishlist] = useState(() => load("hc_wishlist"));
  const [cartOpen, setCartOpen] = useState(false);
  const userId = user?.id || null;
  const userRef = useRef(null);
  useEffect(() => { userRef.current = userId; }, [userId]);

  useEffect(() => { localStorage.setItem("hc_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("hc_wishlist", JSON.stringify(wishlist)); }, [wishlist]);

  // Merge server-side wishlist into local on login, then persist merged list back
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/wishlist");
        const serverIds = data.ids || [];
        const local = load("hc_wishlist");
        const missing = serverIds.filter((id) => !local.some((p) => p.id === id));
        if (!missing.length) {
          if (local.length && local.length !== serverIds.length) {
            api.put("/auth/wishlist", { ids: local.map((p) => p.id) }).catch(() => {});
          }
          return;
        }
        const { data: products } = await api.get(`/products/by-ids?ids=${missing.join(",")}`);
        if (cancelled) return;
        setWishlist((cur) => {
          const merged = [...cur, ...products.filter((p) => !cur.some((c) => c.id === p.id))];
          api.put("/auth/wishlist", { ids: merged.map((p) => p.id) }).catch(() => {});
          return merged;
        });
      } catch { /* offline */ }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const addToCart = useCallback((product, qty = 1) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.product.id === product.id);
      if (existing) {
        return prev.map((i) => i.product.id === product.id ? { ...i, qty: i.qty + qty } : i);
      }
      return [...prev, { product, qty }];
    });
  }, []);

  const setQty = useCallback((productId, qty) => {
    setCart((prev) => qty <= 0
      ? prev.filter((i) => i.product.id !== productId)
      : prev.map((i) => i.product.id === productId ? { ...i, qty } : i));
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCart((prev) => prev.filter((i) => i.product.id !== productId));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const toggleWishlist = useCallback((product) => {
    setWishlist((prev) => {
      const next = prev.find((p) => p.id === product.id)
        ? prev.filter((p) => p.id !== product.id)
        : [...prev, product];
      if (userRef.current) {
        api.put("/auth/wishlist", { ids: next.map((p) => p.id) }).catch(() => {});
      }
      return next;
    });
  }, []);

  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const cartTotal = cart.reduce((s, i) => s + (i.product.sale_price || i.product.price) * i.qty, 0);

  return (
    <CartContext.Provider value={{ cart, wishlist, cartOpen, setCartOpen, addToCart, setQty, removeFromCart, clearCart, toggleWishlist, cartCount, cartTotal }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
